import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "@/data/content";

const LINKS = [
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/who-we-help", label: "Who We Help", testid: "nav-link-who-we-help" },
  { to: "/resources", label: "Resources", testid: "nav-link-resources" },
  { to: "/about", label: "About Anu", testid: "nav-link-about" },
  { to: "/calculators", label: "Calculators", testid: "nav-link-calculators" },
  { to: "/markets", label: "Markets", testid: "nav-link-markets" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass shadow-[0_8px_32px_-16px_rgba(15,23,42,0.25)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
            <img src={COMPANY.logo} alt="Invesmo — Debt Capital Investment and Development Advisory" className="h-14 sm:h-16 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6" data-testid="nav-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-gold-600 ${
                    isActive ? "text-gold-600" : "text-ink/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid="nav-phone-link"
              className="flex items-center gap-2 text-sm font-medium text-ink/80 hover:text-brand transition-colors duration-300"
            >
              <Phone size={15} strokeWidth={2.2} />
              {COMPANY.phoneDisplay}
            </a>
            <Link
              to="/book"
              data-testid="book-meeting-cta"
              className="rounded-full bg-obsidian text-gold px-6 py-2.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:bg-gold hover:text-obsidian hover:shadow-gold-glow"
            >
              Book a Meeting
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-ink"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden glass overflow-hidden border-t border-ink/5"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`mobile-${l.testid}`} className="text-lg font-medium text-ink">
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/book"
                data-testid="mobile-book-meeting-cta"
                className="mt-2 rounded-full bg-obsidian text-gold px-6 py-3 text-center text-sm font-semibold"
              >
                Book a Meeting
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
